import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import { useAtomicStyles } from "styles";
import { useForm } from "react-hook-form";
import { actions, selectors } from "redux-store";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FileManager } from "models";
import { Event } from "models";
import { PatchMerchantsEventsByMerchantIdAndEventIdParams } from "redux-store/extra-actions/apis/patch-merchants-events-by-merchant-id-and-event-id";
import { useRouteMatch } from "react-router-dom";
import { useCurrentMerchantId, useTypedTranslations } from "hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hidden: {
      display: "none",
    },
    mainContainer: {
      backgroundColor: "#eee",
    },
    titleInfo: {
      width: "100%",
      textAlign: "center",
    },
    paper: {
      [theme.breakpoints.up("xs")]: {
        marginTop: "1em",
      },
    },
    contentTypo: {
      minHeight: 131.2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    dataContainer: {
      position: "relative",
    },
    imageContainer: {
      position: "relative",
    },
    backButton: {
      position: "absolute",
      top: 14,
      left: 0,
    },
    closeButton: {
      position: "absolute",
      [theme.breakpoints.down("xs")]: {
        position: "inherit",
      },
      top: 8,
      left: 14,
    },
    saveButton: {
      flexGrow: 1,
      minWidth: 106.64,
    },
    imageButton: {},
    actionButtons: {
      display: "flex",
      flexDirection: "row",
    },
  }),
);

enum Fields {
  TITLE,
  CONTENT,
  VALIDFROM,
  EXPIRE,
}

const schema = yup.object().shape({
  title: yup
    .string()
    .required("campo Obbligatorio")
    .min(3, "Titolo troppo corto")
    .max(30, "Titolo troppo lungo"),
  content: yup.string().optional().max(200, "Contenuto troppo lungo"),
  validFrom: yup.date(),
  expire: yup.date(),
});

type EventEditFormData = {
  title?: string;
  content?: string;
  imageUri?: string;
  validFrom: Moment;
  expire: Moment;
};

const useEventEditForm = (toEdit: boolean, event: Event) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(() => ({}), []);
  const atomicStyles = useAtomicStyles();
  const {
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    formState: { isValid },
  } = useForm<EventEditFormData>({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const [imageB64, setImageB64] = useState("");
  const [edit, setEdit] = useState<number[]>([]);
  const editImage = useSelector(selectors.EditEventDialogState).editImage;
  const dispatch = useDispatch();
  const isLoading = useSelector(
    selectors.getAjaxIsLoadingByApi(
      actions.patchMerchantsEventsByMerchantIdAndEventId.api,
    ),
  );
  const [t] = useTypedTranslations();

  const merchantId = useCurrentMerchantId();

  const goToImageChanger = useCallback(() => {
    dispatch(actions.setEditEventDialogToImage(true));
  }, [dispatch]);

  const handleImageChangerClose = useCallback(() => {
    dispatch(actions.setEditEventDialogToImage(false));
  }, [dispatch]);

  const handleChangeImage = useCallback(
    (newB64: string) => {
      setValue("imageUri", newB64, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setImageB64(newB64);
    },
    [setValue],
  );

  const uploadLogo = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        setValue("imageUri", event.imageUri, {
          shouldValidate: imageB64 !== "",
        });
        setImageB64(event.imageUri);
        return;
      }

      return FileManager.convertFileToBase64(files[0]).then(
        (pictureBase64: string) => {
          handleChangeImage(pictureBase64);
        },
      );
    },
    [handleChangeImage, imageB64, setValue, event.imageUri],
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit((formData) => {
        if (!formData.validFrom) {
          setValue("validFrom", event.validFrom);
          return onSubmit();
        }
        if (!formData.expire) {
          setValue("expire", event.expire);
          return onSubmit();
        }
        const prepareData: PatchMerchantsEventsByMerchantIdAndEventIdParams = {
          merchantId: merchantId,
          eventId: event._id,
          validFrom: formData.validFrom.toISOString(),
          expire: formData.expire.toISOString(),
        };
        if (formData.title !== event.title) {
          prepareData["title"] = formData.title;
        }
        if (formData.content !== event.content) {
          prepareData["content"] = formData.content;
        }
        if (formData.imageUri !== event.imageUri) {
          prepareData["imageUri"] = formData.imageUri;
        }
        dispatch(
          actions.patchMerchantsEventsByMerchantIdAndEventId.request(
            prepareData,
          ),
        );
      }),
    [handleSubmit, event, setValue, dispatch, merchantId],
  );

  const handleEdit = useCallback(
    (callerIndex: number) => {
      setEdit(edit.concat(callerIndex));
    },
    [edit, setEdit],
  );

  const handleDeleteEdit = useCallback(
    (callerIndex: number) => {
      setEdit(edit.filter((el) => el !== callerIndex));
    },
    [edit],
  );

  const modified = useMemo(() => {
    const values = watch();
    for (let el of Object.keys(values)) {
      if (event[el] !== (values[el] ?? event[el])) {
        return true;
      }
    }
    return false;
  }, [watch, event]);

  return {
    styles,
    classes,
    atomicStyles,
    register,
    errors,
    onSubmit,
    watch,
    edit,
    handleEdit,
    handleDeleteEdit,
    control,
    Fields,
    editImage,
    handleImageChangerClose,
    uploadLogo,
    isValid,
    modified,
    isLoading,
    goToImageChanger,
    t,
  };
};

export { useEventEditForm as default };
